import React, { useCallback } from 'react';

import type { Nullable } from 'chaika-frontend-types/frontend-cms';

import { AsyncSelect, OptionType } from '@components/Select';
import {
    EMPTY_IDS,
    LegalEntitySelectScope,
    useLegalEntitySelect,
} from '@organization/hooks/legalEntity/useLegalEntitySelect';
import { isUuid } from '@utils/isUUID';

interface Props extends LegalEntitySelectScope {
    value?: Nullable<string>;
    title?: string;
    placeholder?: string;
    dataTest?: string;
    disabled?: boolean;
    isClearable?: boolean;
    forcePlaceholder?: boolean;
    onChange(option: OptionType<string>): void;
}

export function LegalEntityAsyncSelect({
    types,
    statuses,
    searchTypes,
    searchStatuses,
    value,
    title,
    placeholder,
    dataTest,
    disabled,
    isClearable,
    forcePlaceholder,
    onChange,
}: Props): React.ReactElement {
    const selectedIds = value && isUuid(value) ? [value] : EMPTY_IDS;
    const { defaultOptions, loadOptions, isLoading, primeIds } = useLegalEntitySelect({
        types,
        statuses,
        searchTypes,
        searchStatuses,
        selectedIds,
    });

    const handleChange = useCallback(
        (option: OptionType<string>) => {
            onChange(option);
            if (option.value && isUuid(option.value)) {
                primeIds([option.value]);
            }
        },
        [onChange, primeIds],
    );

    return (
        <AsyncSelect
            dataTest={dataTest}
            disabled={disabled}
            title={title}
            placeholder={placeholder}
            isClearable={isClearable}
            forcePlaceholder={forcePlaceholder}
            selectedValue={value ?? undefined}
            defaultOptions={defaultOptions}
            loadOptions={loadOptions}
            isLoading={isLoading}
            onChange={handleChange}
        />
    );
}
